import { fromJS } from "immutable"
import { createSelector, createStructuredSelector } from "reselect"
import { alterActionCenterIssues } from "shared/grassroots/action-types"
import { TRANSACTION_SUMMARY_TABLE_ID } from "shared/pac/constants"
import { getPacPaths } from "imports/desktopPaths"

export const selectTransactionTableRowData = (state) => state.sheet.getIn([TRANSACTION_SUMMARY_TABLE_ID, "row_data"])

/**
 * Given a list of Transactions, return all reference_transaction
 * resource_uris.
 *
 * @returns {Array} list of transaction resource_uris
 */
export const selectTransactionReferenceUris = createSelector(selectTransactionTableRowData, (tableRowData) =>
    tableRowData ? tableRowData.map((row) => row.get("reference_transaction")).filter((value) => value) : [],
)

const selectFields = (_, props) => props.fields

export const makeFieldSelector = (value, defaultVal) =>
    createSelector(selectFields, (fields) => (fields && fields[value]) || defaultVal)

export const makeFieldSelectorJS = (value, defaultVal) =>
    createSelector(selectFields, (fields) => (fields && fields[value] && fields[value].toJS()) || defaultVal)

export const selectId = makeFieldSelector("id")
export const selectIsP2A = makeFieldSelector("isP2A")
export const selectUrl = makeFieldSelector("url")
export const selectDownloadLink = makeFieldSelector("downloadLink")
export const selectSent = makeFieldSelector("sent")
export const selectActionCenterDict = makeFieldSelectorJS("actionCenterDict")
export const selectExtra = makeFieldSelectorJS("extra")
export const selectExtraImmutable = makeFieldSelector("extra")
export const selectTableId = makeFieldSelector("tableId")
export const selectDataType = makeFieldSelector("dataType")

export const selectActionCenter = makeFieldSelector("actionCenter")
export const selectSegueLinkPath = makeFieldSelector("segueLinkPath")
export const selectIsLockedForEditing = makeFieldSelector("isLockedForEditing")
export const selectUserName = makeFieldSelector("userName")
export const selectIsOrgAdmin = makeFieldSelector("isOrgAdmin")
export const selectPaused = makeFieldSelector("paused")
export const selectActionCenterId = makeFieldSelector("actionCenterId")
export const selectMakeUrl = makeFieldSelector("makeUrl")
export const selectLedgerSettings = makeFieldSelector("ledgerSettings")
export const selectUUID = makeFieldSelector("uuid")

export const selectTransactionType = makeFieldSelector("transaction_type")

export const selectIsPinned = makeFieldSelector("isPinned")
export const selectIsPersonallyPinned = makeFieldSelector("isPersonallyPinned")

export const getIssueCellStateFactory = (actionCenterId) => (rowData) =>
    rowData._extra.action_centers && rowData._extra.action_centers.includes(Number(actionCenterId))
        ? "default"
        : "deactivated"

export const selectIssueCellState = createSelector(
    selectActionCenterId,
    (actionCenterId) => actionCenterId && getIssueCellStateFactory(actionCenterId),
)

export const selectExtraActionCenters = createSelector(selectExtra, (extra) => extra && extra.action_centers)

/**
 * selectExtraActionCenterDomain is used in makePreviewAndCopyLinkColumns <ActionCenterTableHelpers>
 * to generate the url of the given action center.
 */
export const selectExtraActionCenterDomain = createSelector(selectExtra, (extra) => extra && extra.action_center_domain)

export const selectActionCenterEnabled = createSelector(
    selectExtraActionCenters,
    selectActionCenterId,
    (extraActionCenters, actionCenterId) => extraActionCenters && extraActionCenters.includes(actionCenterId),
)

/**
 * newExtra is used in IssueActionCenterToggle <grassroots/components/IssueActionCenterToggle>
 * to either add or remove the current actionCenterId into the IssueCellState.
 */
export const selectNewExtra = createSelector(
    selectExtraImmutable,
    selectActionCenterEnabled,
    selectActionCenterId,
    (extra, enabled, actionCenterId) => {
        if (enabled) {
            return extra.update("action_centers", (centers) => centers.filter((e) => e !== actionCenterId))
        }
        return extra.update("action_centers", (centers) =>
            centers ? centers.push(actionCenterId) : fromJS([actionCenterId]),
        )
    },
)

/**
 * Select the `issue` element for use in selectModifyActionObject. This selects the full issue from the table.
 */
export const selectIssueForModifyObject = (state, props) =>
    props.fields && state.sheet.getIn([props.fields.tableId, "row_data", props.fields.rowIndex])

/**
 * selectModifyActionObj is used in IssueActionCenterToggle <grassroots/components/IssueActionCenterToggle>
 * to fill the parameters for modifyAction in editResourceTableRow's optionalActionCreator.
 */
export const selectModifyActionObj = createSelector(
    selectId,
    selectActionCenterId,
    selectIssueForModifyObject,
    (id, actionCenterId, issueForModifyObject) =>
        fromJS({
            qdt: DjangIO.app.models.QuorumDataType.action_center_settings,
            id: actionCenterId,
            issue: issueForModifyObject,
            overwriteActionTypes: alterActionCenterIssues,
        }),
)

export const selectCSVLink = createSelector(selectDownloadLink, (downloadLink) =>
    downloadLink ? (downloadLink.includes(".zip.zip") ? downloadLink.replace(".zip.zip", ".zip") : downloadLink) : "",
)

export const selectExtraUsername = createSelector(selectExtra, (extra) => extra && extra.user__username)

export const selectExtraPreviewLink = createSelector(selectExtra, (extra) => extra && extra.preview_link)

export const selectActionCenterDomain = createSelector(selectActionCenterDict, (dict) => dict && dict.domain)

export const selectActionCenterResourceId = createSelector(
    selectActionCenter,
    (actionCenter) =>
        actionCenter && DjangIO.app.grassroots.models.ActionCenterSettings.idFromResourceUri(actionCenter),
)

export const selectButtonId = createSelector(selectId, (id) => id && `copy-link-button-${id}`)

export const selectGrassrootsUrl = createSelector(
    selectId,
    selectExtraActionCenterDomain,
    makeFieldSelector("neededAttr"),
    makeFieldSelector("neededValue"),
    selectExtraImmutable,
    selectMakeUrl,
    (id, domain, neededAttr, neededValue, extra, makeUrl) => makeUrl({ domain, id, [neededAttr]: neededValue, extra }),
)

export const selectButtonText = createSelector(
    selectIsPinned,
    selectIsPersonallyPinned,
    (isPinned, isPersonallyPinned) => (isHovered) => {
        if (isHovered) {
            if (isPinned) {
                return "Unpin (All)"
            }
            if (isPersonallyPinned) {
                return "Unpin (Me)"
            }
        } else {
            if (isPinned) {
                return "All"
            }
            if (isPersonallyPinned) {
                return "Me"
            }
        }
    },
)

export const selectShouldRenderButton = createSelector(
    selectIsPinned,
    selectIsPersonallyPinned,
    (isPinned, isPersonallyPinned) => isPinned || isPersonallyPinned,
)

/** @type {(state: any, props: any) => any} */
export const columnGridConnection = createStructuredSelector({
    referenceUris: selectTransactionReferenceUris,
})

export const copyCampaignActionConnection = createStructuredSelector({
    id: selectId,
    domain: selectActionCenterDomain,
    previewLink: selectExtraPreviewLink,
    extraDomain: selectExtraActionCenterDomain,
    neededAttr: makeFieldSelector("neededAttr"),
    neededValue: makeFieldSelector("neededValue"),
    extra: selectExtraImmutable,
    makeUrl: selectMakeUrl,
})

export const previewLinkActionConnection = createStructuredSelector({
    url: selectGrassrootsUrl,
})

export const editCampaignActionConnection = createStructuredSelector({
    id: selectId,
    actionCenterId: selectActionCenterResourceId,
    isP2A: selectIsP2A,
})

export const duplicateRowActionConnection = createStructuredSelector({
    id: selectId,
})

export const basicActionConnection = createStructuredSelector({
    id: selectId,
    tableId: selectTableId,
    segueLinkPath: selectSegueLinkPath,
    disabled: makeFieldSelector("disabled"),
    className: makeFieldSelector("className"),
    dataType: selectDataType,
    isEditDisabled: makeFieldSelector("isEditDisabled"),
})

export const editActionConnection = createStructuredSelector({
    id: selectId,
    tableId: selectTableId,
    segueLinkPath: selectSegueLinkPath,
    disabled: makeFieldSelector("disabled"),
    className: makeFieldSelector("className"),
    dataType: selectDataType,
})

export const templateActionConnection = createStructuredSelector({
    id: selectId,
    tableId: selectTableId,
    segueLinkPath: selectSegueLinkPath,
    disabled: makeFieldSelector("disabled"),
    className: makeFieldSelector("className"),
    dataType: selectDataType,
})

export const editTrackingDashboardActionConnection = createStructuredSelector({
    id: selectId,
    isLockedForEditing: selectIsLockedForEditing,
    userName: selectUserName,
    isOrgAdmin: selectIsOrgAdmin,
    extraUsername: selectExtraUsername,
})

export const pauseAlertActionConnection = createStructuredSelector({
    alertId: selectId,
    paused: selectPaused,
    tableId: makeFieldSelector("tableId"),
})

export const deleteAlertActionConnection = createStructuredSelector({
    alertId: selectId,
    tableId: makeFieldSelector("tableId"),
    disabled: makeFieldSelector("disabled"),
})

export const pinSheetActionConnection = createStructuredSelector({
    datumId: selectId,
    tableId: selectTableId,
    isPinned: selectIsPinned,
    isPersonallyPinned: selectIsPersonallyPinned,
    getButtonText: selectButtonText,
    shouldRenderButton: selectShouldRenderButton,
})

export const issueToggleActionConnection = createStructuredSelector({
    id: selectId,
    tableId: selectTableId,
    issueCellState: selectIssueCellState,
    enabled: selectActionCenterEnabled,
    newExtra: selectNewExtra,
    modifyActionObj: selectModifyActionObj,
})

export const downloadPDFActionConnection = createStructuredSelector({
    id: selectId,
    url: selectUrl,
})

export const downloadCSVActionConnection = createStructuredSelector({
    csvLink: selectCSVLink,
    sent: selectSent,
})

export const downloadMenuActionConnection = createStructuredSelector({
    id: selectId,
    isFinished: makeFieldSelector("isFinished"),
})

export const pacActionConnection = createStructuredSelector({
    id: selectId,
    uuid: selectUUID,
    ledgerSettings: selectLedgerSettings,
})

export const jurisdictionActionConnection = createStructuredSelector({
    id: selectId,
    uuid: selectUUID,
    ledgerSettings: selectLedgerSettings,
})

export const selectRefundPath = createSelector(
    selectTransactionType,
    selectLedgerSettings,
    selectUUID,
    (transactionType, ledgerSettings, uuid) => {
        const path = getPacPaths({
            ledgerSettingsId: ledgerSettings,
            transactionId: uuid,
        })._pacTransactionRefundNew
        return path
    },
)

export const transactionActionConnection = createStructuredSelector({
    disabled: makeFieldSelector("disabled"),
    ledgerSettings: selectLedgerSettings,
    refundPath: selectRefundPath,
    transactionType: selectTransactionType,
    uuid: selectUUID,
})

export const reconciliationActionConnection = createStructuredSelector({
    id: selectId,
})

export const duplicateRegistrationPageActionConnection = createStructuredSelector({
    id: selectId,
    actionCenterId: selectActionCenterId,
})
